import React, { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import io from 'socket.io-client';
import { API_BASE_URL } from '../config';
import generateAvatar from '../utils/generateAvatar';
import '../styles/BadgePrint.css';
import '../styles/BadgeStation.css';

// Admin view component — two tables: Unprinted + Printed
const BadgeStationAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printingUser, setPrintingUser] = useState(null);
  const [printQr, setPrintQr] = useState(null);
  const [search, setSearch] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);

  const getEmployeeId = (user) => {
    if (user.employee_number) return String(user.employee_number).padStart(4, '0');
    return '0000';
  };

  const fetchAll = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/badges/all`);
      const data = await res.json();
      if (data.users) setUsers(data.users);
    } catch (err) {
      console.error('Failed to fetch all badges:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Refresh a user's profile (re-fetch username + profile picture)
  const refreshUser = async (user) => {
    setRefreshingId(user.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/${user.id}`);
      const data = await res.json();
      if (data.userId) {
        setUsers(prev => prev.map(u =>
          u.id === user.id ? {
            ...u,
            username: data.username || u.username,
            profile_picture_url: data.profilePicture || u.profile_picture_url,
          } : u
        ));
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    } finally {
      setRefreshingId(null);
    }
  };

  const handlePrint = async (user) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/profile/${user.id}`);
      const data = await res.json();
      const fullUser = {
        id: data.userId,
        short_id: data.shortId,
        username: data.username,
        profile_picture_url: data.profilePicture,
        employee_number: user.employee_number,
      };

      const portfolioUrl = `https://komok.org/portfolio/${fullUser.id}`;
      const dataUrl = await QRCode.toDataURL(portfolioUrl, {
        width: 200, margin: 1,
        color: { dark: '#000000', light: '#ffffff' },
        errorCorrectionLevel: 'M'
      });

      setPrintingUser(fullUser);
      setPrintQr(dataUrl);

      setTimeout(() => {
        window.print();
        fetch(`${API_BASE_URL}/api/badges/mark-printed`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id }),
        }).then(() => {
          setUsers(prev => prev.map(u =>
            u.id === user.id ? { ...u, badge_printed: true } : u
          ));
        }).catch(() => {});
        setPrintingUser(null);
        setPrintQr(null);
      }, 300);
    } catch (err) {
      console.error('Failed to print badge:', err);
      setPrintingUser(null);
      setPrintQr(null);
    }
  };

  const searchFilter = (u) => {
    if (!search) return true;
    return u.username?.toLowerCase().includes(search.toLowerCase());
  };

  const unprinted = users.filter(u => !u.badge_printed && searchFilter(u));
  const printed = users.filter(u => u.badge_printed && searchFilter(u));

  const renderRow = (user) => (
    <div key={user.id} className="admin-row">
      <span className="col-user">@{user.username || '—'}</span>
      <span className="col-time">
        {user.registered_at
          ? new Date(user.registered_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })
          : '—'}
      </span>
      <span className="col-actions">
        <button className="admin-print-btn" onClick={() => handlePrint(user)}>
          Print
        </button>
        <button
          className="admin-refresh-btn"
          onClick={() => refreshUser(user)}
          disabled={refreshingId === user.id}
          title="Refresh profile picture & username"
        >
          {refreshingId === user.id ? '...' : '↻'}
        </button>
      </span>
    </div>
  );

  const renderBadgeCard = (user) => {
    const photoSrc = user.profile_picture_url
      ? (user.profile_picture_url.startsWith('data:')
          ? user.profile_picture_url
          : `${API_BASE_URL}${user.profile_picture_url}`)
      : generateAvatar(user.username);
    const empId = user.employee_number ? String(user.employee_number).padStart(4, '0') : '0000';
    return (
      <div key={user.id} className="unprinted-badge-card">
        <div className="badge-card-photo">
          <img src={photoSrc} alt={user.username} onError={(e) => { e.target.src = generateAvatar(user.username); }} />
        </div>
        <div className="badge-card-info">
          <div className="badge-card-name">@{user.username || '—'}</div>
          <div className="badge-card-id">ID: #{empId}</div>
        </div>
        <div className="badge-card-actions">
          <button className="admin-print-btn" onClick={() => handlePrint(user)}>Print</button>
          <button
            className="admin-refresh-btn"
            onClick={() => refreshUser(user)}
            disabled={refreshingId === user.id}
          >
            {refreshingId === user.id ? '...' : '↻'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="badge-station-container">
      <div className="station-ui no-print admin-two-tables">
        <div className="station-header">
          <h1>Badge Station</h1>
          <div className="admin-stats">
            <span className="stat-unprinted">{unprinted.length} unprinted</span>
            <span className="stat-printed">{printed.length} printed</span>
            <span className="stat-total">{users.length} total</span>
            <button className="admin-refresh-all-btn" onClick={fetchAll}>Refresh</button>
            <button className="admin-refresh-all-btn" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
          </div>
        </div>

        <div className="admin-controls">
          <input
            type="text"
            className="admin-search"
            placeholder="Search username..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="station-loading"><p>Loading users...</p></div>
        ) : (
          <>
            <div className="admin-table-section">
              <div className="admin-section-title unprinted-title">
                UNPRINTED ({unprinted.length})
              </div>
              {unprinted.length === 0 ? (
                <div className="admin-empty">All badges printed!</div>
              ) : (
                <div className="unprinted-badges-grid">
                  {unprinted.map(renderBadgeCard)}
                </div>
              )}
            </div>

            <div className="admin-table-section" style={{ marginTop: 20 }}>
              <div className="admin-section-title printed-title">
                PRINTED ({printed.length})
              </div>
              <div className="admin-table">
                <div className="admin-table-header">
                  <span className="col-user">User</span>
                  <span className="col-time">Time</span>
                  <span className="col-actions">Actions</span>
                </div>
                {printed.length === 0 ? (
                  <div className="admin-empty">No badges printed yet</div>
                ) : (
                  printed.map(renderRow)
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Badge for printing */}
      {printingUser && printQr && (
        <div className="badge-label">
          <div className="badge-top-bar">
            <div className="bar-pattern"></div>
          </div>
          <div className="badge-header">
            <img src="/komok_logo.png" alt="KOMOK" className="badge-logo" />
          </div>
          <div className="badge-photo-container">
            <img
              src={printingUser.profile_picture_url
                ? (printingUser.profile_picture_url.startsWith('data:')
                    ? printingUser.profile_picture_url
                    : `${API_BASE_URL}${printingUser.profile_picture_url}`)
                : generateAvatar(printingUser.username)}
              alt="Profile"
              className="badge-photo"
            />
          </div>
          <div className="badge-username">@{printingUser.username}</div>
          <div className="badge-divider">
            <span className="divider-text">EMPLOYEE IDENTIFICATION</span>
          </div>
          <div className="badge-employee-id">
            <span className="id-label">ID:</span>
            <span className="id-value">#{getEmployeeId(printingUser)}</span>
          </div>
          <div className="badge-qr-section">
            <img src={printQr} alt="Portfolio QR" className="badge-qr" />
            <div className="qr-label">SCAN TO VIEW PORTFOLIO</div>
          </div>
          <div className="badge-footer">
            <div className="footer-line">"Your productivity is our profit"</div>
            <div className="footer-warning">KOMOK - OUR PARTY IS YOUR PROFIT</div>
          </div>
          <div className="badge-bottom-bar">
            <div className="bar-pattern"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const BadgeStation = () => {
  // Auto-print queue mode only if ?autoprint=true, otherwise show the full table view
  const isAutoprint = new URLSearchParams(window.location.search).get('autoprint') === 'true';
  const isAdmin = !isAutoprint; // Default to table view

  const [currentUser, setCurrentUser] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [printQueue, setPrintQueue] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]); // users waiting to be printed
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const processedIdsRef = useRef(new Set()); // dedup within this session

  const getEmployeeId = (user) => {
    if (user && user.employeeNumber) return String(user.employeeNumber).padStart(4, '0');
    return '0000';
  };

  // Load a user's full profile and prepare their badge for display
  const loadBadge = useCallback(async (userData) => {
    const uid = userData.userId || userData.id;
    if (!uid) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/profile/${uid}`);
      const data = await response.json();

      // /api/profile/:userId returns { userId, shortId, username, profilePicture, hasCompletedProfile }
      if (data.userId) {
        const user = {
          id: data.userId,
          short_id: data.shortId,
          username: data.username,
          profile_picture_url: data.profilePicture,
          employeeNumber: data.employeeNumber,
        };

        const portfolioUrl = `https://komok.org/portfolio/${user.id}`;
        const dataUrl = await QRCode.toDataURL(portfolioUrl, {
          width: 200,
          margin: 1,
          color: { dark: '#000000', light: '#ffffff' },
          errorCorrectionLevel: 'M'
        });

        setCurrentUser(user);
        setQrDataUrl(dataUrl);
      }
    } catch (err) {
      console.error('Error loading badge:', err);
    }
  }, []);

  // Add a user to the pending queue (deduped)
  const enqueueUser = useCallback((userData) => {
    const uid = userData.userId || userData.id;
    if (!uid || processedIdsRef.current.has(uid)) return;
    processedIdsRef.current.add(uid);

    setPendingUsers(prev => {
      if (prev.some(u => (u.userId || u.id) === uid)) return prev;
      return [...prev, userData];
    });
  }, []);

  // Load the first pending user's badge for display
  useEffect(() => {
    if (isAdmin) return;
    if (pendingUsers.length > 0 && !currentUser) {
      loadBadge(pendingUsers[0]);
    }
  }, [pendingUsers, currentUser, loadBadge, isAdmin]);

  // Print current badge and advance to next
  const handlePrint = async () => {
    if (!currentUser) return;

    window.print();

    // Mark badge as printed in the database
    try {
      await fetch(`${API_BASE_URL}/api/badges/mark-printed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.id }),
      });
    } catch (err) {
      console.error('Failed to mark badge as printed:', err);
    }

    // Add to print history
    setPrintQueue(prev => [
      { ...currentUser, printedAt: new Date(), qrDataUrl },
      ...prev.slice(0, 19)
    ]);

    // Remove from pending and clear current
    setPendingUsers(prev => prev.slice(1));
    setCurrentUser(null);
    setQrDataUrl(null);
  };

  // Skip current badge (mark printed without actually printing)
  const handleSkip = async () => {
    if (!currentUser) return;

    try {
      await fetch(`${API_BASE_URL}/api/badges/mark-printed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.id }),
      });
    } catch (err) {
      console.error('Failed to mark badge as printed:', err);
    }

    setPendingUsers(prev => prev.slice(1));
    setCurrentUser(null);
    setQrDataUrl(null);
  };

  useEffect(() => {
    if (isAdmin) return; // Admin mode doesn't use socket/polling

    // Socket connection (primary: instant notifications)
    const socket = io(API_BASE_URL, { transports: ['websocket', 'polling'], auth: { public: true } });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[BadgeStation] Connected to server');
      setConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('[BadgeStation] Disconnected');
      setConnected(false);
    });

    socket.on('user-registered', (data) => {
      console.log('[BadgeStation] User registered (socket):', data);
      enqueueUser(data);
    });

    // Fetch unprinted badges (called immediately + every 5s)
    const fetchUnprinted = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/badges/unprinted`);
        const data = await res.json();
        if (data.users && data.users.length > 0) {
          for (const u of data.users) {
            enqueueUser({ userId: u.id, shortId: u.short_id, username: u.username });
          }
        }
      } catch (err) {
        // Non-critical
      }
    };

    // Poll immediately on mount, then every 5 seconds
    fetchUnprinted();
    const pollInterval = setInterval(fetchUnprinted, 5000);

    return () => {
      socket.disconnect();
      clearInterval(pollInterval);
    };
  }, [enqueueUser, isAdmin]);

  const handleReprint = async (user) => {
    processedIdsRef.current.delete(user.id);
    enqueueUser({ userId: user.id });
  };

  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Render admin view
  if (isAdmin) {
    return <BadgeStationAdmin />;
  }

  return (
    <div className="badge-station-container">
      {!isFs && (
        <button onClick={() => document.documentElement.requestFullscreen()} className="no-print" style={{
          position: 'fixed', top: 8, left: 8, zIndex: 99999,
          background: 'rgba(0,255,65,0.15)', border: '1px solid #00ff41',
          color: '#00ff41', padding: '4px 10px', fontSize: '11px',
          fontFamily: 'Courier New, monospace', cursor: 'pointer', borderRadius: '3px',
        }}>FULLSCREEN</button>
      )}
      {/* Screen UI - hidden when printing */}
      <div className="station-ui no-print">
        <div className="station-header">
          <h1>Badge Print Station <span style={{
            fontSize: '10px', padding: '2px 6px', borderRadius: '3px', marginLeft: '8px',
            background: connected ? 'rgba(0,255,65,0.15)' : 'rgba(255,68,68,0.15)',
            color: connected ? '#00ff41' : '#ff4444',
            border: `1px solid ${connected ? '#00ff41' : '#ff4444'}`
          }}>{connected ? 'LIVE' : 'OFF'}</span></h1>
        </div>

        {!currentUser && pendingUsers.length === 0 && (
          <div className="station-waiting">
            <div className="waiting-icon">&#9201;</div>
            <p>Waiting for registrations...</p>
            <p className="waiting-hint">Badges will print automatically when a guest registers</p>
          </div>
        )}

        {currentUser && (
          <div className="station-ready">
            <h2>Badge ready for <strong>@{currentUser.username}</strong></h2>
            <div className="station-actions">
              <button className="print-btn" onClick={handlePrint}>
                PRINT BADGE
              </button>
              <button className="skip-btn" onClick={handleSkip}>
                Skip
              </button>
            </div>
            {pendingUsers.length > 1 && (
              <p className="queue-count">{pendingUsers.length - 1} more badge(s) waiting</p>
            )}
          </div>
        )}

        {!currentUser && pendingUsers.length > 0 && (
          <div className="station-loading">
            <p>Loading badge data...</p>
          </div>
        )}

        {/* Recent prints */}
        {printQueue.length > 0 && (
          <div className="recent-prints">
            <h3>Recent Prints</h3>
            <div className="print-list">
              {printQueue.map((user, i) => (
                <div key={`${user.id}-${i}`} className="print-item">
                  <span className="print-username">@{user.username}</span>
                  <span className="print-time">
                    {new Date(user.printedAt).toLocaleTimeString()}
                  </span>
                  <button
                    className="reprint-btn"
                    onClick={() => handleReprint(user)}
                    disabled={!!currentUser}
                  >
                    Reprint
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* The badge to print - only visible during print */}
      {currentUser && qrDataUrl && (
        <div className="badge-label">
          <div className="badge-top-bar">
            <div className="bar-pattern"></div>
          </div>

          <div className="badge-header">
            <img src="/komok_logo.png" alt="KOMOK" className="badge-logo" />
          </div>

          <div className="badge-photo-container">
            <img
              src={currentUser.profile_picture_url
                ? (currentUser.profile_picture_url.startsWith('data:')
                    ? currentUser.profile_picture_url
                    : `${API_BASE_URL}${currentUser.profile_picture_url}`)
                : generateAvatar(currentUser.username)}
              alt="Profile"
              className="badge-photo"
            />
          </div>

          <div className="badge-username">@{currentUser.username}</div>

          <div className="badge-divider">
            <span className="divider-text">EMPLOYEE IDENTIFICATION</span>
          </div>

          <div className="badge-employee-id">
            <span className="id-label">ID:</span>
            <span className="id-value">#{getEmployeeId(currentUser)}</span>
          </div>

          <div className="badge-qr-section">
            <img src={qrDataUrl} alt="Portfolio QR" className="badge-qr" />
            <div className="qr-label">SCAN TO VIEW PORTFOLIO</div>
          </div>

          <div className="badge-footer">
            <div className="footer-line">"Your productivity is our profit"</div>
            <div className="footer-warning">KOMOK - OUR PARTY IS YOUR PROFIT</div>
          </div>

          <div className="badge-bottom-bar">
            <div className="bar-pattern"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadgeStation;
